// vpn-web-bo/src/pages/promo-codes/index.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Table, Space, Tag, Popconfirm, App, Input } from 'antd';
import { PlusOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { authFetch } from '../../utils/api';
import { PromoCodeFormModal } from './PromoCodeFormModal';
import { BatchGenerateModal } from './BatchGenerateModal';
import { useViewScope } from '../../utils/viewScope';
import { targetBrandFromRow } from '../../shared/form/writeTargetBrand';

export interface PromoCodeRecord {
  id: number;
  /**
   * 去品牌化 M4：這筆兌換碼 row 的實際品牌（後端清單回傳 vpn_promo_code.vpn_brand）。
   * 編輯 / 刪除 = 既有 row 寫入，目標品牌 = row.brand，顯式帶 X-Target-Brand，
   * 絕不繼承檢視範圍 / activeBrand。
   */
  brand: string;
  code: string;
  name: string;
  status: 'active' | 'disabled';
  grantMb: number;
  /** VPN-81：有效時長（秒，權威單位）。 */
  validitySeconds: number;
  perDeviceCapMb: number;
  /** VPN-81 B2：單裝置兌換次數上限（0=不限）。 */
  perDeviceRedeemLimit?: number;
  /** VPN-81 B5：節點等級 ACL 白名單（null/省略=不限）。 */
  allowedNodeTiers?: number[] | null;
  totalRedemptionLimit: number;
  redeemedCount: number;
  startAt: string | null;
  endAt: string | null;
}

/** 把秒數格式化成人話（天/小時/分/秒），列表顯示用。 */
export function formatValiditySeconds(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return '—';
  if (sec % 86400 === 0) return `${sec / 86400} 天`;
  if (sec % 3600 === 0) return `${sec / 3600} 小時`;
  if (sec % 60 === 0) return `${sec / 60} 分鐘`;
  return `${sec} 秒`;
}

export const PromoCodeList: React.FC = () => {
  // React 19 下 antd 靜態 `message.*` 不渲染 → 用 App context 綁定的 message。
  const { message } = App.useApp();
  // 檢視範圍（去品牌化 M1）：兌換碼清單跟著 Header 檢視走。
  const { brandForRequest } = useViewScope();
  const [data, setData] = useState<PromoCodeRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [editing, setEditing] = useState<PromoCodeRecord | null>(null);
  // VPN-99/VPN-100：搜尋關鍵字（後端 list 已支援 keyword → code/name LIKE，
  // 且兩個 LIKE 分支都帶品牌 scope，搜尋不繞過品牌隔離）。
  const [keyword, setKeyword] = useState('');
  // 搜尋後把前端分頁拉回第 1 頁，避免停在超出結果數的空白頁。
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const kw = keyword.trim();
      const res = await authFetch(
        `/api/admin/promo-codes?page=${page}&pageSize=${pageSize}${
          kw ? `&keyword=${encodeURIComponent(kw)}` : ''
        }`
      );
      const result = await res.json();
      if (result.code === 0) {
        setData(result.data?.items ?? []);
        setTotal(result.data?.total ?? 0);
      } else {
        // fail-closed：讀取被拒（如品牌未開放兌換碼管理 403）→ 清空，
        // 不殘留上一個檢視範圍的 stale 清單。
        setData([]);
        setTotal(0);
      }
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
    // 檢視範圍、搜尋字或分頁變更時，向後端重拉對應的兌換碼清單。
  }, [brandForRequest, keyword, page, pageSize]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (record: PromoCodeRecord) => {
    // M4：兌換碼刪除為既有 row 寫入。清單回傳每筆 row 的 brand 欄 → 目標品牌 =
    // row.brand，顯式帶 X-Target-Brand，絕不繼承檢視範圍 / activeBrand。對齊 backend
    // resolveExistingRowWriteTargetBrand（顯式與 row 不符 → 403、缺顯式 enforce ON → 400）。
    const t = targetBrandFromRow(record.brand);
    if (!t.ok) {
      message.warning(t.reason);
      return;
    }
    const res = await authFetch(`/api/admin/promo-codes/${record.id}`, {
      method: 'DELETE',
      headers: { 'X-Target-Brand': t.brand },
    });
    const r = await res.json();
    if (r.code === 0) {
      message.success('已刪除');
      fetchData();
    } else message.error(r.msg || '刪除失敗');
  };

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            // M4：聚合檢視也可新增 — modal 內「目標品牌」欄位強制顯式選擇。
            setEditing(null);
            setFormOpen(true);
          }}
        >
          新增兌換碼
        </Button>
        <Button
          icon={<ThunderboltOutlined />}
          onClick={() => setBatchOpen(true)}
        >
          批量產碼
        </Button>
        <Input.Search
          allowClear
          placeholder="搜尋兌換碼 / 名稱"
          style={{ width: 240 }}
          onSearch={v => {
            // VPN-99/VPN-100：按兌換碼 code + 名稱搜尋（後端 keyword LIKE，
            // 品牌檢視範圍內過濾）。allowClear 清空也走 onSearch('') 還原全列表。
            setKeyword(v);
            setPage(1);
          }}
        />
        <span style={{ color: '#999' }}>共 {total} 組</span>
      </Space>
      <Table
        rowKey="id"
        loading={loading}
        dataSource={data}
        pagination={{
          current: page,
          pageSize,
          // 後端為 server-side pagination；未提供 total 時，AntD 只會以本頁
          // dataSource 長度計算頁數，127 筆資料會被誤判成只有一頁。
          total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100', '500'],
          showTotal: count => `共 ${count} 組`,
          onChange: (nextPage, nextPageSize) => {
            if (nextPageSize !== pageSize) {
              setPageSize(nextPageSize);
              setPage(1);
              return;
            }
            setPage(nextPage);
          },
        }}
        columns={[
          { title: '兌換碼', dataIndex: 'code', width: 160 },
          { title: '名稱', dataIndex: 'name' },
          {
            title: '狀態',
            dataIndex: 'status',
            width: 90,
            render: (s: string) => (
              <Tag color={s === 'active' ? 'green' : 'default'}>
                {s === 'active' ? '啟用' : '停用'}
              </Tag>
            ),
          },
          { title: '發放(MB)', dataIndex: 'grantMb', width: 100 },
          {
            title: '有效時長',
            dataIndex: 'validitySeconds',
            width: 100,
            render: (s: number) => formatValiditySeconds(s),
          },
          {
            title: '兌換/上限',
            width: 110,
            render: (_: unknown, r: PromoCodeRecord) =>
              `${r.redeemedCount}/${r.totalRedemptionLimit === 0 ? '∞' : r.totalRedemptionLimit}`,
          },
          {
            title: '操作',
            width: 140,
            render: (_: unknown, r: PromoCodeRecord) => (
              <Space>
                <Button
                  type="link"
                  size="small"
                  onClick={() => {
                    // M4：編輯 = 既有 row 寫入，目標品牌固定 row.brand，聚合檢視也可編。
                    setEditing(r);
                    setFormOpen(true);
                  }}
                >
                  編輯
                </Button>
                <Popconfirm
                  title="確認刪除此兌換碼？"
                  onConfirm={() => handleDelete(r)}
                >
                  <Button type="link" danger size="small">
                    刪除
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      <PromoCodeFormModal
        open={formOpen}
        record={editing}
        // M4：檢視品牌只做「目標品牌」欄位預填（聚合=null 留空強制手選），不當寫入來源。
        viewBrand={brandForRequest}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSuccess={() => {
          setFormOpen(false);
          setEditing(null);
          fetchData();
        }}
      />
      <BatchGenerateModal
        open={batchOpen}
        // M4：檢視品牌只做「目標品牌」欄位預填（聚合=null 留空強制手選），不當寫入來源。
        viewBrand={brandForRequest}
        onClose={() => setBatchOpen(false)}
        onSuccess={() => {
          setBatchOpen(false);
          fetchData();
        }}
      />
    </div>
  );
};
export default PromoCodeList;
